import { useEffect, useState } from 'react';

import { Alert, Box, Button, CircularProgress, Container, Paper, Snackbar, Tab, Tabs, Typography } from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';

import { useAuthRedirect } from '../../hooks/useAuthRedirect';
import { useAuth } from '../AuthContext';
import { OrganizationsTable, PersonsTable } from './components';
import { useOrganizations } from './hooks/useOrganizations';
import { usePersons } from './hooks/usePersons';

/** Seul compte autorisé à lancer « Sync Organisations » (même liste côté backend). */
const SYNC_ORGANIZATIONS_ALLOWED_EMAILS = ['francois@skipcar.fr'];

const OrganizationsPersonsList = () => {
  const { currentUser, idToken } = useAuth();
  const canSyncOrganizations = SYNC_ORGANIZATIONS_ALLOWED_EMAILS.includes((currentUser?.email ?? '').toLowerCase());
  const { redirectToLogin } = useAuthRedirect();
  const [activeTab, setActiveTab] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isSyncingStagesPipelines, setIsSyncingStagesPipelines] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  const organizationsData = useOrganizations();
  const personsData = usePersons();

  useEffect(() => {
    if (!currentUser) {
      redirectToLogin();
    }
  }, [currentUser, redirectToLogin]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleSyncWithPipedrive = async () => {
    setIsSyncing(true);

    try {
      const response = await fetch('/api/pipedrive/sync-organizations', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
      });

      const data = await response.json();

      if (response.ok) {
        setSnackbarMessage(
          `Synchronisation réussie ! ${data.organizations} organisations et ${data.persons} personnes synchronisées.`,
        );
        setSnackbarSeverity('success');
        setSnackbarOpen(true);

        // Rafraîchir les données après la synchronisation
        organizationsData.handleSearchChange(organizationsData.searchQuery);
        personsData.handleSearchChange(personsData.searchQuery);
      } else {
        setSnackbarMessage(`Erreur lors de la synchronisation: ${data.error || 'Erreur inconnue'}`);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } catch (error: any) {
      setSnackbarMessage(`Erreur lors de la synchronisation: ${error.message || 'Erreur réseau'}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleSyncStagesPipelines = async () => {
    setIsSyncingStagesPipelines(true);

    try {
      const response = await fetch('/api/pipedrive/sync-stages-pipelines', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (response.ok) {
        setSnackbarMessage(
          `Synchronisation réussie ! ${data.pipelines} pipelines et ${data.stages} stages synchronisés.`,
        );
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      } else {
        setSnackbarMessage(`Erreur lors de la synchronisation: ${data.error || 'Erreur inconnue'}`);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } catch (error: any) {
      setSnackbarMessage(`Erreur lors de la synchronisation: ${error.message || 'Erreur réseau'}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsSyncingStagesPipelines(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const isLoading = activeTab === 0 ? organizationsData.isLoading : personsData.isLoading;

  if (isLoading) {
    return (
      <Container maxWidth={false}>
        <Box display="flex" justifyContent="center" alignItems="center" marginTop={10}>
          <CircularProgress size={70} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth={false}>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ marginTop: 4 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Organisations et Personnes
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Gestion des organisations et personnes synchronisées depuis Pipedrive.
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="contained"
            startIcon={isSyncingStagesPipelines ? <CircularProgress size={20} color="inherit" /> : <SyncIcon />}
            onClick={handleSyncStagesPipelines}
            disabled={isSyncingStagesPipelines}
            sx={{
              backgroundColor: '#5fa8fd',
              color: '#ffffff',
              fontWeight: 600,
              padding: '10px 24px',
              borderRadius: '8px',
              textTransform: 'none',
              fontSize: '15px',
              boxShadow: '0 4px 12px rgba(95, 168, 253, 0.3)',
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: '#4a96e8',
                boxShadow: '0 6px 16px rgba(95, 168, 253, 0.4)',
                transform: 'translateY(-2px)',
              },
              '&:active': {
                transform: 'translateY(0)',
              },
              '&:disabled': {
                backgroundColor: '#b8d8f5',
                color: '#ffffff',
              },
            }}
          >
            {isSyncingStagesPipelines ? 'Synchronisation...' : 'Sync Stages & Pipelines'}
          </Button>
          {canSyncOrganizations && (
            <Button
              variant="contained"
              startIcon={isSyncing ? <CircularProgress size={20} color="inherit" /> : <SyncIcon />}
              onClick={handleSyncWithPipedrive}
              disabled={isSyncing}
              sx={{
                backgroundColor: '#fdd15f',
                color: '#ffffff',
                fontWeight: 600,
                padding: '10px 24px',
                borderRadius: '8px',
                textTransform: 'none',
                fontSize: '15px',
                boxShadow: '0 4px 12px rgba(253, 209, 95, 0.3)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: '#e8c054',
                  boxShadow: '0 6px 16px rgba(253, 209, 95, 0.4)',
                  transform: 'translateY(-2px)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                },
                '&:disabled': {
                  backgroundColor: '#f5e5b8',
                  color: '#ffffff',
                },
              }}
            >
              {isSyncing ? 'Synchronisation...' : 'Sync Organisations'}
            </Button>
          )}
        </Box>
      </Box>

      <Paper sx={{ marginTop: 3, padding: 0 }} elevation={3}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          className="DealsListTabs"
          sx={{ justifyContent: 'center', borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label={`Organizations (${organizationsData.totalRawCount})`} />
          <Tab label={`Personnes (${personsData.totalRawCount})`} />
        </Tabs>

        {activeTab === 0 && (
          <OrganizationsTable
            organizations={organizationsData.organizations}
            totalCount={organizationsData.totalCount}
            searchQuery={organizationsData.searchQuery}
            onSearchChange={organizationsData.handleSearchChange}
            sortColumn={organizationsData.sortColumn}
            sortOrder={organizationsData.sortOrder}
            onSort={organizationsData.handleSort}
            page={organizationsData.page}
            rowsPerPage={organizationsData.rowsPerPage}
            onPageChange={organizationsData.handlePageChange}
            onRowsPerPageChange={organizationsData.handleRowsPerPageChange}
          />
        )}

        {activeTab === 1 && (
          <PersonsTable
            persons={personsData.persons}
            totalCount={personsData.totalCount}
            searchQuery={personsData.searchQuery}
            onSearchChange={personsData.handleSearchChange}
            sortColumn={personsData.sortColumn}
            sortOrder={personsData.sortOrder}
            onSort={personsData.handleSort}
            page={personsData.page}
            rowsPerPage={personsData.rowsPerPage}
            onPageChange={personsData.handlePageChange}
            onRowsPerPageChange={personsData.handleRowsPerPageChange}
          />
        )}
      </Paper>

      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default OrganizationsPersonsList;
